import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Share2,
  Instagram,
  Facebook,
  Twitter,
  Linkedin,
  Youtube,
  TrendingUp,
  TrendingDown,
  Users,
  Heart,
  MessageCircle,
  Eye,
  Calendar,
  BarChart3,
  Loader2,
  Link2,
  ExternalLink,
  RefreshCw
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

const SocialDashboardPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Mock data - in production would come from social APIs
  const [socialData, setSocialData] = useState({
    accounts: [],
    overview: {
      total_followers: 0,
      total_engagement: 0,
      posts_this_month: 0,
      reach_this_month: 0
    },
    posts: []
  });

  useEffect(() => {
    fetchSocialData();
  }, []);

  const fetchSocialData = async () => {
    setLoading(true);
    // Simulate API call - in production this would fetch real data
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setSocialData({
      accounts: [
        { platform: 'instagram', handle: '@msc_agence', followers: 12500, growth: 5.2, connected: true },
        { platform: 'facebook', handle: 'MSC Agence', followers: 8700, growth: 2.1, connected: true },
        { platform: 'linkedin', handle: 'MSC L\'Agence', followers: 3200, growth: 8.4, connected: true },
        { platform: 'twitter', handle: '@msc_agence', followers: 1800, growth: -0.5, connected: false },
        { platform: 'youtube', handle: 'MSC Agence', followers: 950, growth: 12.3, connected: false }
      ],
      overview: {
        total_followers: 27150,
        total_engagement: 4.8,
        posts_this_month: 24,
        reach_this_month: 156000
      },
      posts: [
        { id: 1, platform: 'instagram', content: 'Nouvelle réalisation pour notre client...', likes: 234, comments: 18, date: '2024-12-23' },
        { id: 2, platform: 'facebook', content: 'Découvrez notre nouveau service de...', likes: 156, comments: 12, date: '2024-12-22' },
        { id: 3, platform: 'linkedin', content: 'Nous sommes fiers d\'annoncer...', likes: 89, comments: 7, date: '2024-12-21' }
      ]
    });
    setLoading(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSocialData();
    setRefreshing(false);
    toast.success('Données actualisées');
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'instagram': return Instagram;
      case 'facebook': return Facebook;
      case 'twitter': return Twitter;
      case 'linkedin': return Linkedin;
      case 'youtube': return Youtube;
      default: return Share2;
    }
  };

  const getPlatformColor = (platform) => {
    switch (platform) {
      case 'instagram': return '#E4405F';
      case 'facebook': return '#1877F2';
      case 'twitter': return '#1DA1F2';
      case 'linkedin': return '#0A66C2';
      case 'youtube': return '#FF0000';
      default: return '#64748b';
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn("transition-all duration-300 p-6 md:p-8 pt-24", collapsed ? "md:ml-20" : "md:ml-64")}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3" style={{ color: theme.colors?.primary }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${theme.colors?.accent}20` }}>
                <Share2 className="w-5 h-5" style={{ color: theme.colors?.accent }} />
              </div>
              Dashboard Réseaux Sociaux
            </h1>
            <p className="text-[#64748b] mt-1">
              Vue d'ensemble de votre présence sociale
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
              Actualiser
            </Button>
            <Button style={{ backgroundColor: theme.colors?.accent }}>
              <Link2 className="w-4 h-4 mr-2" />
              Connecter un compte
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#64748b]" />
          </div>
        ) : (
          <>
            {/* Overview Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-50">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#1a365d]">{formatNumber(socialData.overview.total_followers)}</p>
                      <p className="text-xs text-[#64748b]">Abonnés totaux</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-pink-50">
                      <Heart className="w-5 h-5 text-pink-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#1a365d]">{socialData.overview.total_engagement}%</p>
                      <p className="text-xs text-[#64748b]">Taux d'engagement</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-50">
                      <Calendar className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#1a365d]">{socialData.overview.posts_this_month}</p>
                      <p className="text-xs text-[#64748b]">Posts ce mois</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-50">
                      <Eye className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#1a365d]">{formatNumber(socialData.overview.reach_this_month)}</p>
                      <p className="text-xs text-[#64748b]">Portée ce mois</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Social Accounts */}
              <Card className="card lg:col-span-2">
                <CardHeader>
                  <CardTitle>Comptes connectés</CardTitle>
                  <CardDescription>Statistiques par plateforme</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {socialData.accounts.map(account => {
                      const Icon = getPlatformIcon(account.platform);
                      const color = getPlatformColor(account.platform);
                      
                      return (
                        <div key={account.platform} className="flex items-center gap-4 p-4 rounded-xl border border-[#e2e8f0]">
                          <div 
                            className="w-12 h-12 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: `${color}15` }}
                          >
                            <Icon className="w-6 h-6" style={{ color }} />
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-[#1a365d]">{account.handle}</span>
                              {account.connected ? (
                                <Badge className="bg-green-100 text-green-700 text-xs">Connecté</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-600 text-xs">Non connecté</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-1">
                              <span className="text-sm text-[#64748b]">
                                <strong className="text-[#1a365d]">{formatNumber(account.followers)}</strong> abonnés
                              </span>
                              <span className={cn(
                                "text-xs flex items-center gap-1",
                                account.growth >= 0 ? "text-green-600" : "text-red-500"
                              )}>
                                {account.growth >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                {account.growth >= 0 ? '+' : ''}{account.growth}%
                              </span>
                            </div>
                          </div>

                          {account.connected ? (
                            <Button variant="ghost" size="sm">
                              <BarChart3 className="w-4 h-4 mr-1" />
                              Stats
                            </Button>
                          ) : (
                            <Button variant="outline" size="sm" style={{ borderColor: color, color }}>
                              <Link2 className="w-4 h-4 mr-1" />
                              Connecter
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Posts */}
              <Card className="card">
                <CardHeader>
                  <CardTitle>Posts récents</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {socialData.posts.map(post => {
                      const Icon = getPlatformIcon(post.platform);
                      const color = getPlatformColor(post.platform);
                      
                      return (
                        <div key={post.id} className="p-3 rounded-lg bg-[#f8fafc]">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon className="w-4 h-4" style={{ color }} />
                            <span className="text-xs text-[#94a3b8]">{post.date}</span>
                          </div>
                          <p className="text-sm text-[#1a365d] line-clamp-2 mb-2">{post.content}</p>
                          <div className="flex items-center gap-4 text-xs text-[#64748b]">
                            <span className="flex items-center gap-1">
                              <Heart className="w-3 h-3" />
                              {post.likes}
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageCircle className="w-3 h-3" />
                              {post.comments}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <Button variant="outline" className="w-full mt-4" size="sm">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Voir tous les posts
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Info Banner */}
            <Card className="card mt-6">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-amber-100">
                    <Share2 className="w-6 h-6 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#1a365d]">Connectez vos réseaux sociaux</h3>
                    <p className="text-sm text-[#64748b]">
                      Pour afficher des données en temps réel, connectez vos comptes sociaux via les APIs officielles de chaque plateforme.
                    </p>
                  </div>
                  <Button style={{ backgroundColor: theme.colors?.accent }}>
                    En savoir plus
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default SocialDashboardPage;
